import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
// importing packages
import { SignoffNavigator } from '../../../../packages/ui/templates/sign-off/signoff-navigator';
import { SignoffHeader } from '../../../../packages/ui/templates/sign-off/signoff-header';
import { SessionNote } from '../../../../packages/ui/templates/sign-off/components/session-note';
import { Button, btnSize, btnType } from '../../../../packages/ui/atoms/button';
import { Icons } from '../../../../packages/icons';
import { colors } from '../../../../packages/colors';
// redux stuff
import { priorityActionCreators } from '../../../../redux/modules/priority';
import { profileActionCreators } from '../../../../redux/modules/profile';
import { showSnackbar } from '../../../../redux/modules/snackbar';
import { PatchAction } from '../../../../redux/modules/appointment/types';
import { getAuth } from '../../../../redux/modules/auth/selectors';
// APIs import
import { getAppointmentById, patchAppointment } from '../../../../services/appointment/appointment.service';
import { getCareTeamMember } from '../../../../services/member/member.service';
// Utils
import history from '../../../../utils/history';
import { sx } from './SessionSignOffV4.styles';
import { Sections, STEP_COMPLETION_STATUS, initialStepsStatus } from './SessionSignOffV4.constant';
// Sections/sign off components
import { FeedbackForUsSection } from './sections/FeedbackForUsSection';
import { FeedbackForYouSection } from './sections/FeedbackForYouSection';
import Giggle from '../giggle';
import { TreatmentPlanSection } from './sections/TreatmentPlanSection';
import { SignOffModal } from '../../member-appointment-notes/components/signoff-modal/SignOffModal';
import dayjs from 'dayjs';
import { getUserTimeZone } from '../../../../utils/dayjs';
import {
  billingRequirementsSection,
  feedbackForUsSection,
  feedbackForYouSection,
  Subsections,
} from '../../../../packages/ui/templates/sign-off/signoff.constants';
import { ProfileInfoRole } from '../../../../components/v2/ProfileInfo';
import { commonActionCreators } from '../../../../redux/modules/common';
import { MemberDrawerController } from '../../../../components/member-drawer-controller';
import { CptCodesSection } from './sections/CptCodesSection';
import { fetchSessionReportCard } from '../../../../services/report-service/reportService.service';
import {
  EmptyState,
  LoadingState,
  TranscriptionOffState,
  RightPanel,
  PanelHeader,
  PanelBody,
  PanelFooter,
} from './components/RightPanel';
import { TreatmentPlanRecommendations } from './sections/components/TreatmentPlanRecommendations';
import { Reasoning } from '../../../../packages/ui/templates/reasoning';
import ReasoningIcon from '../../../../assets/images/bulb.png';

const SessionSignOffV4 = () => {
  const dispatch = useDispatch();
  const { appointmentId } = useParams();
  const { meta } = useSelector(getAuth);

  const [appointment, setAppointment] = useState(null);
  const [careTeamList, setCareTeamList] = useState([]);
  const [selectedSection, setSelectedSection] = useState(Sections.SESSION_NOTE);
  const [selectedSubSection, setSelectedSubSection] = useState(null);
  const [hasReportCard, setHasReportCard] = useState(false);
  const [sessionNoteText, setSessionNoteText] = useState('');
  const [stepsStatus, setStepsStatus] = useState(initialStepsStatus);
  const [visibleSections, setVisibleSections] = useState(Object.keys(initialStepsStatus));
  const [progressBarValue, setProgressBarValue] = useState(0);
  const [showPreviousBtn, setShowPreviousBtn] = useState(false);
  const [showNextBtn, setShowNextBtn] = useState(true);
  const [showSignOffBtn, setShowSignOffBtn] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [openGiggle, setOpenGiggle] = useState(false);
  const [openSignOffModal, setOpenSignOffModal] = useState(false);
  const [isSigningOff, setIsSigningOff] = useState(false);
  const [feedbackForYouSectionSubSection, setFeedbackForYouSectionSubSection] = useState([]);
  const [feedbackForUsSectionSubSection, setFeedbackForUsSectionSubSection] = useState([]);
  const [billingRequirementsSectionSubSection, setBillingRequirementsSectionSubSection] = useState([]);
  const [reasoningNote, setReasoningNote] = useState(null);
  const [isReasoningOpen, setIsReasoningOpen] = useState(false);
  // Right panel state - for treatment plan recommendations
  const [treatmentPlanRecommendations, setTreatmentPlanRecommendations] = useState([]);
  const [treatmentPlanHandlers, setTreatmentPlanHandlers] = useState({
    onApply: null,
    onApplyAll: null,
  });
  // Interactive treatment plan state
  const [interactiveTreatmentPlanState, setInteractiveTreatmentPlanState] = useState({
    isInteractive: false,
    currentStep: null,
    rationale: null,
    onSaveStep: null,
  });
  const [isInteractiveLoading, setIsInteractiveLoading] = useState(false);
  const giggleKeywords = ['giggle', 'laugh', 'chuckle'];
  const getRandomKeyword = () => giggleKeywords[Math.floor(Math.random() * giggleKeywords.length)];

  // Determine visible sections based on role
  useEffect(() => {
    if (appointment) {
      const role = appointment?.practitionerDesignation;
      let sectionsToShow = Object.keys(initialStepsStatus);

      // Hide Treatment Plan for Care Managers
      if (role === ProfileInfoRole.CARE_MANAGER) {
        sectionsToShow = sectionsToShow.filter(section => section !== Sections.TREATMENT_PLAN);
      }

      setVisibleSections(sectionsToShow);

      // Reset steps status to include only visible sections
      const newStepsStatus = {};
      sectionsToShow.forEach(section => {
        newStepsStatus[section] = stepsStatus[section] || STEP_COMPLETION_STATUS.NOT_STARTED;
      });
      setStepsStatus(newStepsStatus);

      if (!appointment.sessionAnalysisAllowed) {
        const subSections = feedbackForYouSection.filter(sub => sub !== Subsections.REPORT_CARD);
        setFeedbackForYouSectionSubSection(subSections);
      } else {
        if (hasReportCard) {
          setFeedbackForYouSectionSubSection(feedbackForYouSection);
        } else {
          const subSections = feedbackForYouSection.filter(sub => sub !== Subsections.REPORT_CARD);
          setFeedbackForYouSectionSubSection(subSections);
        }
      }

      if (role === ProfileInfoRole.NURSE_PRACTITIONER || role === ProfileInfoRole.PHYSICIAN) {
        setFeedbackForUsSectionSubSection(feedbackForUsSection);
      } else {
        setFeedbackForUsSectionSubSection(
          feedbackForUsSection.filter(sub => sub !== Subsections.REVIEW_MEDICATIONS),
        );
      }
      if (
        role === ProfileInfoRole.THERAPIST ||
        role === ProfileInfoRole.NURSE_PRACTITIONER ||
        role === ProfileInfoRole.PHYSICIAN
      ) {
        setBillingRequirementsSectionSubSection(billingRequirementsSection);
      } else {
        setBillingRequirementsSectionSubSection(
          billingRequirementsSection.filter(sub => sub !== Subsections.REVIEW_DIAGNOSIS),
        );
      }
    }
  }, [appointment, hasReportCard]);

  // Fetch care team members
  const getCareTeamList = () => {
    if (appointment?.patientId) {
      getCareTeamMember({ userId: appointment.patientId }, { careNavigator: true })
        .then(careTeamListResponse => {
          if (careTeamListResponse?.status === 200) {
            setCareTeamList(careTeamListResponse?.data || []);
          } else {
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: 'Whoops ! Something went wrong . Please try later',
              }),
            );
          }
        })
        .catch(error => {
          console.log('Whoops ! Something went wrong . Please try later', error);
        });
    }
  };

  // Fetch Session Report Card
  const fetchSessionReport = () => {
    fetchSessionReportCard({ appointmentId: appointment.appointmentId })
      .then(() => {
        setHasReportCard(true);
      })
      .catch(() => {
        setHasReportCard(false);
      });
  };

  useEffect(() => {
    dispatch(priorityActionCreators.fetchPriorityDomainTypes());

    // Fetch appointment details
    if (appointmentId) void fetchAppointmentsDetails();
  }, []);

  useEffect(() => {
    if (appointment?.patientId) {
      dispatch(profileActionCreators.fetchDomainTypes(appointment?.patientId));
      getCareTeamList();
      fetchSessionReport();
    }
  }, [appointment]);

  // Mark current section as completed
  useEffect(() => {
    setStepsStatus(prev => ({
      ...prev,
      [selectedSection]: STEP_COMPLETION_STATUS.COMPLETED,
    }));
  }, [selectedSection]);

  // Update progress bar and navigation buttons
  useEffect(() => {
    const totalVisibleSteps = visibleSections.length;
    const completedCount = visibleSections.filter(
      section => stepsStatus[section] === STEP_COMPLETION_STATUS.COMPLETED,
    ).length;

    const isLastSection = visibleSections.indexOf(selectedSection) === visibleSections.length - 1;

    if (isLastSection) {
      setProgressBarValue(100);
    } else {
      setProgressBarValue((completedCount / totalVisibleSteps) * 100);
    }

    const allCompleted = visibleSections.every(
      section => stepsStatus[section] === STEP_COMPLETION_STATUS.COMPLETED,
    );
    setShowSignOffBtn(allCompleted || isLastSection);

    const currentIndex = visibleSections.indexOf(selectedSection);
    // Hide Previous button during interactive mode
    setShowPreviousBtn(currentIndex > 0 && !interactiveTreatmentPlanState.isInteractive);
    setShowNextBtn(!isLastSection);
  }, [stepsStatus, selectedSection, visibleSections]);

  const fetchAppointmentsDetails = async () => {
    try {
      const { data } = await getAppointmentById({ appointmentId });
      setAppointment(data);
      if (data?.sessionNote) {
        setSessionNoteText(data.sessionNote);
        setStepsStatus(prev => ({
          ...prev,
          [Sections.SESSION_NOTE]: STEP_COMPLETION_STATUS.COMPLETED,
        }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleBack = () => {
    history.go(-1);
  };

  const handleFinishLater = async () => {
    if (selectedSection === Sections.SESSION_NOTE && sessionNoteText) {
      await handleSessionNoteSave();
    }
    history.go(-1);
  };

  const handleSignOff = () => {
    const allCompleted = visibleSections.every(
      section => stepsStatus[section] === STEP_COMPLETION_STATUS.COMPLETED,
    );

    if (allCompleted) {
      setOpenSignOffModal(true);
    } else {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Cannot sign off, not all steps are completed',
        }),
      );
    }
  };

  const performSignOff = async (skip = false) => {
    if (isSigningOff) return;

    setIsSigningOff(true);
    try {
      const isSupervisor = appointment?.signOffStatus === 'REVIEW';

      if (isSupervisor) {
        await patchAppointment(
          {
            action: PatchAction.ADD_SUPERVISOR_NOTES,
            supervisorNotesData: {
              notes: '',
              status: 'APPROVED',
            },
          },
          {
            appointmentId: appointment.appointmentId,
          },
        );
      } else {
        await patchAppointment(
          {
            action: PatchAction.PROVIDER_SIGN_OFF,
            skipProviderSignOff: skip,
          },
          {
            appointmentId: appointment.appointmentId,
          },
        );
      }

      setOpenSignOffModal(false);
      history.push(
        `/provider/${meta?.userId}/day-schedule?date=${dayjs().tz(getUserTimeZone()).format('YYYY-MM-DD')}`,
      );
    } catch (error) {
      console.error('Error during sign off:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Error during sign off. Please try again.',
        }),
      );
    } finally {
      setIsSigningOff(false);
    }
  };

  const handleSessionNoteSave = async () => {
    if (!appointment?.appointmentId) {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Appointment ID is missing',
        }),
      );
      return;
    }

    setIsSavingNotes(true);
    try {
      const response = await patchAppointment(
        { action: PatchAction.ADD_SESSION_NOTE, sessionNoteRequestDto: { sessionNote: sessionNoteText } },
        { appointmentId: appointment.appointmentId },
      );

      if (response.status === 200) {
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Session notes saved successfully',
          }),
        );
      }
    } catch (error) {
      console.error('Failed to save session notes:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save session notes. Please try again.',
        }),
      );
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleSessionNoteChange = content => {
    setSessionNoteText(content);
  };

  const handleMemberActionClick = type => {
    dispatch(commonActionCreators.openDrawer(type));
  };

  const handlePrevious = () => {
    // Block Previous during interactive mode
    if (interactiveTreatmentPlanState.isInteractive) {
      return;
    }

    const currentIndex = visibleSections.indexOf(selectedSection);
    if (currentIndex <= 0) return;

    const goToPreviousMainSection = () => {
      const previousSection = visibleSections[currentIndex - 1];

      const updatedStatus = { ...stepsStatus };
      for (let i = currentIndex; i < visibleSections.length; i++) {
        updatedStatus[visibleSections[i]] = STEP_COMPLETION_STATUS.NOT_STARTED;
      }
      setStepsStatus(updatedStatus);

      setSelectedSection(previousSection);
      setSelectedSubSection(null);
    };

    if (selectedSection === Sections.FEEDBACK_FOR_US) {
      if (!selectedSubSection) {
        goToPreviousMainSection();
        return;
      }

      const subIndex = feedbackForUsSectionSubSection.indexOf(selectedSubSection);

      if (subIndex < 0) {
        goToPreviousMainSection();
        return;
      }

      if (subIndex === 0) {
        setSelectedSection(Sections.BILLING_REQUIREMENTS);
        setSelectedSubSection(
          billingRequirementsSectionSubSection[billingRequirementsSectionSubSection.length - 1],
        );
        return;
      }

      let prevSubIndex = subIndex - 1;
      while (
        prevSubIndex >= 0 &&
        !shouldShowSubsection(
          feedbackForUsSectionSubSection[prevSubIndex],
          appointment?.practitionerDesignation,
        )
      ) {
        prevSubIndex--;
      }

      if (prevSubIndex >= 0) {
        setSelectedSubSection(feedbackForUsSectionSubSection[prevSubIndex]);
      } else {
        goToPreviousMainSection();
      }
      return;
    }

    if (selectedSection === Sections.FEEDBACK_FOR_YOU) {
      const updatedStatus = { ...stepsStatus };
      updatedStatus[Sections.FEEDBACK_FOR_YOU] = STEP_COMPLETION_STATUS.NOT_STARTED;
      setStepsStatus(updatedStatus);
      if (!selectedSubSection) {
        setSelectedSection(Sections.FEEDBACK_FOR_US);

        let lastVisibleSubIndex = feedbackForUsSectionSubSection.length - 1;
        while (
          lastVisibleSubIndex >= 0 &&
          !shouldShowSubsection(
            feedbackForUsSectionSubSection[lastVisibleSubIndex],
            appointment?.practitionerDesignation,
          )
        ) {
          lastVisibleSubIndex--;
        }

        if (lastVisibleSubIndex >= 0) {
          setSelectedSubSection(feedbackForUsSectionSubSection[lastVisibleSubIndex]);
        }
        return;
      }

      const subIndex = feedbackForYouSectionSubSection.indexOf(selectedSubSection);
      if (subIndex < 0) {
        setSelectedSection(Sections.FEEDBACK_FOR_US);
        let lastVisibleSubIndex = feedbackForUsSectionSubSection.length - 1;
        while (
          lastVisibleSubIndex >= 0 &&
          !shouldShowSubsection(
            feedbackForUsSectionSubSection[lastVisibleSubIndex],
            appointment?.practitionerDesignation,
          )
        ) {
          lastVisibleSubIndex--;
        }

        if (lastVisibleSubIndex >= 0) {
          setSelectedSubSection(feedbackForUsSectionSubSection[lastVisibleSubIndex]);
        }
        return;
      } else if (subIndex === 0) {
        let lastVisibleSubIndex = feedbackForUsSectionSubSection.length - 1;
        while (
          lastVisibleSubIndex >= 0 &&
          !shouldShowSubsection(
            feedbackForUsSectionSubSection[lastVisibleSubIndex],
            appointment?.practitionerDesignation,
          )
        ) {
          lastVisibleSubIndex--;
        }
        setSelectedSection(Sections.FEEDBACK_FOR_US);
        if (lastVisibleSubIndex >= 0) {
          setSelectedSubSection(feedbackForUsSectionSubSection[lastVisibleSubIndex]);
        }
      } else {
        setSelectedSubSection(feedbackForYouSectionSubSection[subIndex - 1]);
      }
      return;
    }

    if (selectedSection === Sections.BILLING_REQUIREMENTS) {
      if (!selectedSubSection) {
        goToPreviousMainSection();
        return;
      }

      const subIndex = billingRequirementsSectionSubSection.indexOf(selectedSubSection);

      if (subIndex < 0) {
        goToPreviousMainSection();
        return;
      }

      if (subIndex === 0) {
        goToPreviousMainSection();
        return;
      }

      let prevSubIndex = subIndex - 1;
      while (
        prevSubIndex >= 0 &&
        !shouldShowSubsection(
          billingRequirementsSectionSubSection[prevSubIndex],
          appointment?.practitionerDesignation,
        )
      ) {
        prevSubIndex--;
      }

      if (prevSubIndex >= 0) {
        setSelectedSubSection(billingRequirementsSectionSubSection[prevSubIndex]);
      } else {
        goToPreviousMainSection();
      }
      return;
    }

    goToPreviousMainSection();
  };

  const handleSkipInteractive = () => {
    // Exit interactive mode and proceed to next section
    setInteractiveTreatmentPlanState({
      isInteractive: false,
      currentStep: null,
      rationale: null,
      onSaveStep: null,
    });
    setIsNextDisabled(false);

    // Move to next section
    const currentIndex = visibleSections.indexOf(selectedSection);
    const nextSection = visibleSections[currentIndex + 1];
    if (nextSection) {
      setSelectedSection(nextSection);
      setSelectedSubSection(null);
    }
  };

  const handleNext = async () => {
    let subSections = feedbackForYouSection;
    const currentIndex = visibleSections.indexOf(selectedSection);
    if (currentIndex === -1) return;

    if (selectedSection === Sections.SESSION_NOTE && sessionNoteText && !appointment?.waitingForAI) {
      await handleSessionNoteSave();
    }

    // Handle interactive treatment plan mode - save current step before proceeding
    if (selectedSection === Sections.TREATMENT_PLAN && interactiveTreatmentPlanState.isInteractive) {
      if (interactiveTreatmentPlanState.onSaveStep) {
        await interactiveTreatmentPlanState.onSaveStep();
      }
      // Don't proceed to next section - stay in treatment plan until interactive flow is complete
      return;
    }

    const goToNextMainSection = (subSections = null) => {
      const nextSection = visibleSections[currentIndex + 1];
      setSelectedSection(nextSection);
      setSelectedSubSection(null);
    };

    if (selectedSection === Sections.BILLING_REQUIREMENTS) {
      if (!selectedSubSection) {
        const role = appointment?.practitionerDesignation;
        let availableSubsections = [Subsections.REVIEW_CPT_CODES];

        if (
          role === ProfileInfoRole.THERAPIST ||
          role === ProfileInfoRole.NURSE_PRACTITIONER ||
          role === ProfileInfoRole.PHYSICIAN
        ) {
          availableSubsections.push(Subsections.REVIEW_DIAGNOSIS);
        }

        availableSubsections = availableSubsections
          .filter(sub => billingRequirementsSectionSubSection.includes(sub))
          .sort(
            (a, b) =>
              billingRequirementsSectionSubSection.indexOf(a) -
              billingRequirementsSectionSubSection.indexOf(b),
          );

        if (availableSubsections.length > 0) {
          setSelectedSubSection(availableSubsections[0]);
          return;
        } else {
          goToNextMainSection();
          return;
        }
      }

      const subIndex = billingRequirementsSectionSubSection.indexOf(selectedSubSection);
      if (subIndex === -1) return;

      let nextSubIndex = subIndex + 1;
      while (
        nextSubIndex < billingRequirementsSectionSubSection.length &&
        !shouldShowSubsection(
          billingRequirementsSectionSubSection[nextSubIndex],
          appointment?.practitionerDesignation,
        )
      ) {
        nextSubIndex++;
      }

      const nextSub =
        nextSubIndex < billingRequirementsSectionSubSection.length
          ? billingRequirementsSectionSubSection[nextSubIndex]
          : null;

      if (nextSub) {
        setSelectedSubSection(nextSub);
      } else {
        goToNextMainSection();
      }
      return;
    }

    if (selectedSection === Sections.FEEDBACK_FOR_US) {
      if (!selectedSubSection) {
        const role = appointment?.practitionerDesignation;
        let availableSubsections = [Subsections.UPCOMING_APPOINTMENTS];

        if (
          role === ProfileInfoRole.THERAPIST ||
          role === ProfileInfoRole.NURSE_PRACTITIONER ||
          role === ProfileInfoRole.PHYSICIAN
        ) {
          availableSubsections.push(Subsections.REVIEW_DIAGNOSIS);
        }

        if (role === ProfileInfoRole.NURSE_PRACTITIONER || role === ProfileInfoRole.PHYSICIAN) {
          availableSubsections.push(Subsections.REVIEW_MEDICATIONS);
        }

        availableSubsections = availableSubsections
          .filter(sub => feedbackForUsSectionSubSection.includes(sub))
          .sort(
            (a, b) => feedbackForUsSectionSubSection.indexOf(a) - feedbackForUsSectionSubSection.indexOf(b),
          );

        if (availableSubsections.length > 0) {
          setSelectedSubSection(availableSubsections[0]);
          return;
        } else {
          goToNextMainSection();
          return;
        }
      }

      const subIndex = feedbackForUsSectionSubSection.indexOf(selectedSubSection);
      if (subIndex === -1) return;

      let nextSubIndex = subIndex + 1;
      while (
        nextSubIndex < feedbackForUsSectionSubSection.length &&
        !shouldShowSubsection(
          feedbackForUsSectionSubSection[nextSubIndex],
          appointment?.practitionerDesignation,
        )
      ) {
        nextSubIndex++;
      }

      const nextSub =
        nextSubIndex < feedbackForUsSectionSubSection.length
          ? feedbackForUsSectionSubSection[nextSubIndex]
          : null;

      if (nextSub) {
        setSelectedSubSection(nextSub);
      } else {
        goToNextMainSection();
      }
      return;
    }

    if (selectedSection === Sections.FEEDBACK_FOR_YOU) {
      const subIndex = feedbackForYouSectionSubSection.indexOf(selectedSubSection);
      if (subIndex === -1) return;
      const nextSub = subSections[subIndex + 1];
      if (nextSub) {
        setSelectedSubSection(nextSub);
      } else {
        goToNextMainSection();
      }
      return;
    }

    goToNextMainSection(subSections);
  };

  const handleSectionChange = async section => {
    if (selectedSection === Sections.SESSION_NOTE && sessionNoteText) {
      await handleSessionNoteSave();
    }

    const newIndex = visibleSections.indexOf(section);
    const currentIndex = visibleSections.indexOf(selectedSection);

    const updatedStatus = { ...stepsStatus };

    if (newIndex > currentIndex) {
      for (let i = 0; i <= newIndex; i++) {
        updatedStatus[visibleSections[i]] = STEP_COMPLETION_STATUS.COMPLETED;
      }
      setStepsStatus(updatedStatus);
    } else if (newIndex < currentIndex) {
      for (let i = newIndex + 1; i < visibleSections.length; i++) {
        updatedStatus[visibleSections[i]] = STEP_COMPLETION_STATUS.NOT_STARTED;
      }
      setStepsStatus(updatedStatus);
    }

    setSelectedSection(section);
    setSelectedSubSection(null);
  };

  const shouldShowSubsection = (subsection, role) => {
    if (subsection === Subsections.REVIEW_DIAGNOSIS) {
      return (
        role === ProfileInfoRole.THERAPIST ||
        role === ProfileInfoRole.NURSE_PRACTITIONER ||
        role === ProfileInfoRole.PHYSICIAN
      );
    }
    if (subsection === Subsections.REVIEW_MEDICATIONS) {
      return role === ProfileInfoRole.NURSE_PRACTITIONER || role === ProfileInfoRole.PHYSICIAN;
    }
    return true;
  };

  const renderSectionContent = () => {
    switch (selectedSection) {
      case Sections.SESSION_NOTE:
        return (
          <SessionNote
            appointment={appointment}
            text={sessionNoteText}
            handleChange={handleSessionNoteChange}
          />
        );
      case Sections.TREATMENT_PLAN:
        return (
          <TreatmentPlanSection
            memberId={appointment?.patientId}
            providerId={appointment?.practitionerId}
            openGiggle={() => setOpenGiggle(true)}
            appointmentId={appointmentId}
            practitionerDesignation={appointment?.practitionerDesignation}
            // waitingForAI={appointment?.waitingForAI}
            onRecommendationsChange={(recs, onApply, onApplyAll) => {
              setTreatmentPlanRecommendations(recs);
              setTreatmentPlanHandlers({ onApply, onApplyAll });
            }}
            onInteractiveStateChange={(isInteractive, currentStep, onSaveStep, canProceed) => {
              setInteractiveTreatmentPlanState(prev => ({
                ...prev,
                isInteractive,
                currentStep,
                onSaveStep,
              }));
              // Disable Next button if interactive and can't proceed (but not if loading)
              setIsNextDisabled(isInteractive && !canProceed);
            }}
            onInteractiveRationaleChange={rationale => {
              setInteractiveTreatmentPlanState(prev => ({
                ...prev,
                rationale,
              }));
            }}
            onInteractiveLoadingChange={isLoading => {
              setIsInteractiveLoading(isLoading);
              // Disable buttons during loading
              if (isLoading) {
                setIsNextDisabled(true);
              }
            }}
          />
        );
      case Sections.BILLING_REQUIREMENTS:
        return (
          <CptCodesSection
            selectedSubSection={selectedSubSection}
            setSelectedSubSection={setSelectedSubSection}
            appointment={appointment}
            openGiggle={() => setOpenGiggle(true)}
            role={appointment?.practitionerDesignation}
            handleOpenReasoning={handleOpenNoteReasoning}
          />
        );
      case Sections.FEEDBACK_FOR_US:
        return (
          <FeedbackForUsSection
            selectedSubSection={selectedSubSection}
            setSelectedSubSection={setSelectedSubSection}
            appointment={appointment}
            openGiggle={() => setOpenGiggle(true)}
          />
        );
      case Sections.FEEDBACK_FOR_YOU:
        return (
          <FeedbackForYouSection
            selectedSubSection={selectedSubSection}
            setSelectedSubSection={setSelectedSubSection}
            appointment={appointment}
            openGiggle={() => setOpenGiggle(true)}
            handleOpenReasoning={handleOpenNoteReasoning}
            waitingForAI={appointment?.waitingForAI}
          />
        );
      default:
        return (
          <FeedbackForYouSection
            selectedSubSection={selectedSubSection}
            setSelectedSubSection={setSelectedSubSection}
            appointment={appointment}
            openGiggle={() => setOpenGiggle(true)}
            handleOpenReasoning={handleOpenNoteReasoning}
            waitingForAI={appointment?.waitingForAI}
          />
        );
    }
  };

  const handleOpenNoteReasoning = note => {
    setReasoningNote(note);
    setIsReasoningOpen(true);
  };

  const handleCloseReasoning = () => {
    setIsReasoningOpen(false);
    setReasoningNote(null);
  };

  const handleCloseInteractiveReasoning = () => {
    setInteractiveTreatmentPlanState(prev => ({
      ...prev,
      rationale: null,
    }));
  };

  // Render right panel content based on current section
  const renderRightPanelContent = () => {
    // Session Note section - check transcription state
    if (selectedSection === Sections.SESSION_NOTE) {
      if (appointment?.waitingForAI) {
        return (
          <RightPanel>
            <PanelBody>
              <LoadingState
                message="Give us 1 min"
                description="Note creation isn't finished yet. Please give us a few minutes"
                onBackToTodos={() => history.push('/provider/todos')}
              />
            </PanelBody>
          </RightPanel>
        );
      }
      if (!appointment.sessionAnalysisAllowed && !appointment.waitingForAI) {
        return (
          <RightPanel variant="error">
            <PanelBody>
              <TranscriptionOffState />
            </PanelBody>
          </RightPanel>
        );
      }
      return (
        <RightPanel>
          <PanelBody>
            <EmptyState openGiggle={() => setOpenGiggle(true)} />
          </PanelBody>
        </RightPanel>
      );
    }

    // Treatment Plan section - show recommendations or interactive rationale
    if (selectedSection === Sections.TREATMENT_PLAN) {
      // Check for waiting state (AI processing)
      if (appointment?.waitingForAI) {
        return (
          <RightPanel>
            <PanelBody>
              <LoadingState
                message="Give us 1 min"
                description="Treatment plan update isn't finished yet. Please give us a few minutes."
                onBackToTodos={() => history.push('/provider/todos')}
              />
            </PanelBody>
          </RightPanel>
        );
      }

      // Interactive mode - show loading or rationale
      if (interactiveTreatmentPlanState.isInteractive) {
        // Show loading state while fetching goals/objectives
        if (isInteractiveLoading) {
          return (
            <RightPanel>
              <PanelBody>
                <LoadingState
                  message="Give us 1 min"
                  description="Treatment plan update isn't finished yet. Please give us a few minutes."
                  onBackToTodos={() => history.push('/provider/todos')}
                />
              </PanelBody>
            </RightPanel>
          );
        }

        if (interactiveTreatmentPlanState.rationale) {
          return (
            <RightPanel>
              <PanelHeader
                icon={<img src={ReasoningIcon} alt="bulb" width={35} height={35} />}
                title={interactiveTreatmentPlanState.rationale.title}
              />
              <PanelBody>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '36px' }}>
                  {interactiveTreatmentPlanState.rationale.items?.map(item => {
                    return (
                      <Box key={item.id} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {item.title && (
                          <Typography sx={{ fontWeight: '600', fontSize: '16px', lineHeight: '24px' }}>
                            {item.title}
                          </Typography>
                        )}
                        <Typography sx={{ fontWeight: '500', fontSize: '14px', lineHeight: '20px' }}>
                          {item.description}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </PanelBody>
              <PanelFooter>
                <Button
                  variant={btnType.TEXT}
                  onClick={handleCloseInteractiveReasoning}
                  sx={sx.closeReasoning}
                >
                  Close the reasoning
                </Button>
              </PanelFooter>
            </RightPanel>
          );
        }

        return (
          <RightPanel>
            <PanelBody>
              <EmptyState openGiggle={() => setOpenGiggle(true)} />
            </PanelBody>
          </RightPanel>
        );
      }

      // Regular mode - show recommendations with header and footer
      const hasRecs = treatmentPlanRecommendations?.length > 0;

      if (!hasRecs) {
        return (
          <RightPanel>
            <PanelBody>
              <EmptyState openGiggle={() => setOpenGiggle(true)} />
            </PanelBody>
          </RightPanel>
        );
      }

      return (
        <RightPanel>
          <PanelHeader
            icon="magic-wand"
            title="Our recommendations"
            count={treatmentPlanRecommendations.length}
          />
          <PanelBody>
            <TreatmentPlanRecommendations
              recommendations={treatmentPlanRecommendations}
              hasRecommendations={hasRecs}
              onApplyRecommendation={treatmentPlanHandlers.onApply}
              onApplyAllRecommendations={treatmentPlanHandlers.onApplyAll}
            />
          </PanelBody>
          <PanelFooter>
            <Button
              variant={btnType.OUTLINE}
              onClick={() =>
                treatmentPlanHandlers.onApplyAll &&
                treatmentPlanHandlers.onApplyAll(treatmentPlanRecommendations)
              }
              sx={sx.useAllRecommendationsButton}
            >
              Use all recommendations
            </Button>
          </PanelFooter>
        </RightPanel>
      );
    }

    if (selectedSection === Sections.BILLING_REQUIREMENTS) {
      return (
        <RightPanel>
          {isReasoningOpen && (
            <PanelHeader
              icon={<img src={ReasoningIcon} alt="bulb" width={35} height={35} />}
              title={`${reasoningNote?.name || 'Note'} reasoning`}
            />
          )}
          <PanelBody>
            {isReasoningOpen ? (
              <Reasoning note={reasoningNote} isDiagnosis={true} />
            ) : (
              <EmptyState openGiggle={() => setOpenGiggle(true)} />
            )}
          </PanelBody>
          {isReasoningOpen && (
            <PanelFooter>
              <Button variant={btnType.TEXT} onClick={handleCloseReasoning} sx={sx.closeReasoning}>
                Close the reasoning
              </Button>
            </PanelFooter>
          )}
        </RightPanel>
      );
    }

    if (selectedSection === Sections.FEEDBACK_FOR_YOU) {
      return (
        <RightPanel>
          {isReasoningOpen && (
            <PanelHeader
              icon={<img src={ReasoningIcon} alt="bulb" width={35} height={35} />}
              title={`${reasoningNote?.name || 'Note'} reasoning`}
            />
          )}
          <PanelBody>
            {isReasoningOpen ? (
              <Reasoning note={reasoningNote} isDiagnosis={false} />
            ) : (
              <EmptyState openGiggle={() => setOpenGiggle(true)} />
            )}
          </PanelBody>
          {isReasoningOpen && (
            <PanelFooter>
              <Button variant={btnType.TEXT} onClick={handleCloseReasoning} sx={sx.closeReasoning}>
                Close the reasoning
              </Button>
            </PanelFooter>
          )}
        </RightPanel>
      );
    }

    // All other sections show empty state
    return (
      <RightPanel>
        <PanelBody>
          <EmptyState openGiggle={() => setOpenGiggle(true)} />
        </PanelBody>
      </RightPanel>
    );
  };

  // If appointment is still loading
  if (!appointment) {
    return (
      <Box sx={sx.loading}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={sx.root}>
      {/* Main container with padding - contains left and right sections */}
      <Box sx={sx.mainContainer}>
        {/* Left section: Header + Navigator + Content + Footer */}
        <Box sx={sx.leftSection}>
          {/* Header inside left section */}
          <SignoffHeader
            appointment={appointment}
            onFinsh={handleFinishLater}
            onSignOff={handleSignOff}
            onBack={handleBack}
            progressBarValue={progressBarValue}
            selectedSection={selectedSection}
            selectedSubSection={selectedSubSection}
            feedbackForYouSectionSubSection={feedbackForYouSectionSubSection}
          />

          {/* Navigator and content wrapper */}
          <Box sx={sx.leftContentWrapper}>
            <SignoffNavigator
              selectedSection={selectedSection}
              hasReportCard={hasReportCard}
              setSelectedSection={handleSectionChange}
              selectedSubSection={selectedSubSection}
              setSelectedSubSection={setSelectedSubSection}
              onMemberActionClick={handleMemberActionClick}
              practitionerDesignation={appointment?.practitionerDesignation}
              appointment={appointment}
              isInteractiveMode={interactiveTreatmentPlanState.isInteractive}
              visibleSections={visibleSections}
            />
            <Box display="flex" flexDirection="column" flexGrow={1}>
              <Box sx={sx.mainContent}>{renderSectionContent()}</Box>
            </Box>
          </Box>

          {/* Footer with action buttons */}
          <Box sx={sx.actionsContainer}>
            <Box display="flex" flexDirection="row" gap="12px">
              {showPreviousBtn && (
                <Button
                  disabled={interactiveTreatmentPlanState.isInteractive}
                  variant={btnType.OUTLINE}
                  sx={sx.previousButton}
                  onClick={handlePrevious}
                >
                  Previous
                </Button>
              )}
              {interactiveTreatmentPlanState.isInteractive && (
                <Button
                  disableRipple
                  variant={btnType.TEXT}
                  size={btnSize.XSMALL}
                  sx={sx.skipButton}
                  onClick={handleSkipInteractive}
                  disabled={isInteractiveLoading}
                >
                  Skip
                </Button>
              )}
            </Box>
            <Box display="flex" gap="12px">
              {selectedSection === 'feedback-for-you' &&
              selectedSubSection ===
                feedbackForYouSectionSubSection[feedbackForYouSectionSubSection?.length - 1] ? (
                <Button endIcon={<Icons glyph="check" />} sx={sx.nextButton} onClick={handleSignOff}>
                  Sign off
                </Button>
              ) : (
                <Button
                  endIcon={
                    isSavingNotes && (
                      <Icons className="rotate linear infinite" glyph="in-progress" color={colors.white} />
                    )
                  }
                  sx={sx.nextButton}
                  onClick={handleNext}
                  disabled={isNextDisabled}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right section: Recommendations/Reasoning panel */}
        <Box sx={sx.rightSection}>{renderRightPanelContent()}</Box>
      </Box>

      <MemberDrawerController
        appointment={appointment}
        careTeamList={careTeamList}
        renderButtons={false}
        chatIconGlyph="chat"
      />
      <Giggle
        open={openGiggle}
        setOpen={setOpenGiggle}
        keyword={getRandomKeyword()}
        heading={"Let's giggle!"}
      />
      <SignOffModal
        open={openSignOffModal}
        onClose={() => setOpenSignOffModal(false)}
        isLoading={isSigningOff}
        onSubmit={performSignOff}
      />
    </Box>
  );
};

export { SessionSignOffV4 };
